import { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Search, Pencil, Trash2, User, Building2 } from 'lucide-react';

import { QuoteLayout } from '@/components/quotes/QuoteLayout';
import { useQuoteEntitlement } from '@/hooks/useQuoteEntitlement';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const API_URL = process.env.REACT_APP_API_URL;

function emptyForm() {
  return {
    customer_type: 'person',
    name: '',
    contact_name: '',
    email: '',
    phone: '',
    address: '',
    country_code: 'TR',
    notes: '',
  };
}

export function QuoteCustomers() {
  const { t } = useTranslation('quotes');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const { entitlement, refresh: refreshEntitlement } = useQuoteEntitlement();
  const customerLimitReached = entitlement?.limits?.customer_limit != null
    && (entitlement.usage?.customer_count ?? 0) >= entitlement.limits.customer_limit;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/quote/customers`, {
        params: { q: query || undefined, page_size: 200 },
      });
      setItems(data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      customer_type: c.customer_type || 'person',
      name: c.name || '',
      contact_name: c.contact_name || '',
      email: c.email || '',
      phone: c.phone || '',
      address: c.address || '',
      country_code: c.country_code || 'TR',
      notes: c.notes || '',
    });
    setDialogOpen(true);
  };

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error(t('customers.form.validationName'));
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API_URL}/api/quote/customers/${editing.id}`, form);
      } else {
        await axios.post(`${API_URL}/api/quote/customers`, form);
      }
      toast.success(t('common.saved'));
      setDialogOpen(false);
      load();
      refreshEntitlement();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'object' ? (detail.message || t('common.error')) : (detail || t('common.error')));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(t('customers.deleteConfirm', { name: c.name }))) return;
    try {
      await axios.delete(`${API_URL}/api/quote/customers/${c.id}`);
      toast.success(t('customers.deactivatedToast'));
      load();
      refreshEntitlement();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    }
  };

  const reactivate = async (c) => {
    if (!window.confirm(t('customers.activateConfirm', { name: c.name }))) return;
    try {
      await axios.put(`${API_URL}/api/quote/customers/${c.id}`, { is_active: true });
      toast.success(t('customers.activatedToast'));
      load();
      refreshEntitlement();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'object' ? (detail.message || t('common.error')) : (detail || t('common.error')));
    }
  };

  return (
    <QuoteLayout>
      <div className="max-w-6xl mx-auto" data-testid="quote-customers-page">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#1A1A2E]">{t('customers.title')}</h1>
            <p className="text-sm text-[#595959] mt-1">{t('customers.subtitle')}</p>
          </div>
          <Button onClick={openNew} disabled={customerLimitReached} className="bg-[#2563EB] hover:bg-[#1E4ED8]" data-testid="new-customer-btn">
            <Plus className="w-4 h-4 mr-2" />
            {t('customers.newButton')}
          </Button>
        </div>

        {entitlement && <div className="mb-4 text-sm text-[#475569]">
          {t('upgrade.customers')}: {entitlement.usage?.customer_count ?? 0} / {entitlement.limits?.customer_limit ?? t('upgrade.unlimited')}
          {customerLimitReached && <span className="ml-3 text-amber-800">
            {t('upgrade.customerLimitReached')} <Link className="text-blue-600 underline font-semibold" to="/quote-upgrade">{t('upgrade.upgradeLabel')}</Link>
          </span>}
        </div>}

        <form
          onSubmit={(e) => { e.preventDefault(); load(); }}
          className="mb-4 flex gap-2"
        >
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('customers.searchPlaceholder')}
              className="pl-9"
              data-testid="customer-search-input"
            />
          </div>
          <Button type="submit" variant="outline">{t('common.search')}</Button>
        </form>

        <div className="bg-white rounded-2xl border border-[#E5E5E5] overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-[#595959]">{t('common.loading')}</div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center text-[#94A3B8]" data-testid="customers-empty">{t('customers.empty')}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F8FAFC] text-[#475569]">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">{t('customers.columns.name')}</th>
                    <th className="text-left px-4 py-3 font-medium">{t('customers.columns.type')}</th>
                    <th className="text-left px-4 py-3 font-medium">{t('customers.columns.phone')}</th>
                    <th className="text-left px-4 py-3 font-medium">{t('customers.columns.email')}</th>
                    <th className="text-left px-4 py-3 font-medium">{t('customers.columns.status')}</th>
                    <th className="text-right px-4 py-3 font-medium">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F1F5F9]">
                  {items.map((c) => (
                    <tr key={c.id} data-testid={`customer-row-${c.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {c.customer_type === 'company'
                            ? <Building2 className="w-4 h-4 text-[#2563EB]" />
                            : <User className="w-4 h-4 text-[#2563EB]" />}
                          <span className="font-medium text-[#1A1A2E]">{c.name}</span>
                        </div>
                        {c.contact_name && (
                          <p className="text-xs text-[#94A3B8]">{c.contact_name}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-[#595959]">
                        {c.customer_type === 'company' ? t('customers.form.company') : t('customers.form.person')}
                      </td>
                      <td className="px-4 py-3 text-[#595959]">{c.phone || '—'}</td>
                      <td className="px-4 py-3 text-[#595959]">{c.email || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs ${c.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-[#F1F5F9] text-[#94A3B8]'}`}>
                          {c.is_active ? t('common.active') : t('common.inactive')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`edit-customer-${c.id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          {c.is_active ? (
                            <Button variant="ghost" size="icon" onClick={() => remove(c)} data-testid={`delete-customer-${c.id}`}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          ) : (
                            <Button variant="ghost" onClick={() => reactivate(c)} className="text-xs" data-testid={`activate-customer-${c.id}`}>
                              {t('common.activate')}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent data-testid="customer-dialog">
            <DialogHeader>
              <DialogTitle>{editing ? t('customers.form.titleEdit') : t('customers.form.titleNew')}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <Label>{t('customers.form.type')}</Label>
                <Select
                  value={form.customer_type}
                  onValueChange={(v) => setForm({ ...form, customer_type: v })}
                >
                  <SelectTrigger data-testid="customer-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="person">{t('customers.form.person')}</SelectItem>
                    <SelectItem value="company">{t('customers.form.company')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label>{t('customers.form.name')}</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="customer-name-input" />
              </div>
              <div>
                <Label>{t('customers.form.contactName')}</Label>
                <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="customer-contact-input" />
              </div>
              <div>
                <Label>{t('customers.form.country')}</Label>
                <Input maxLength={2} value={form.country_code} onChange={(e) => setForm({ ...form, country_code: e.target.value.toUpperCase() })} />
              </div>
              <div>
                <Label>{t('customers.form.email')}</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="customer-email-input" />
              </div>
              <div>
                <Label>{t('customers.form.phone')}</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="customer-phone-input" />
              </div>
              <div className="md:col-span-2">
                <Label>{t('customers.form.address')}</Label>
                <Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label>{t('customers.form.notes')}</Label>
                <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
              <Button
                onClick={submit}
                disabled={saving}
                className="bg-[#2563EB] hover:bg-[#1E4ED8]"
                data-testid="customer-save-btn"
              >
                {saving ? t('common.saving') : t('common.save')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </QuoteLayout>
  );
}

export default QuoteCustomers;
